import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { User } from 'lucide-react';
import { atletasAPI } from '@/lib/api';

const categorias = ['Todas', 'Adulto', 'Juvenil', 'Mirim', 'Comissão Técnica'];

export default function Equipe() {
  const [atletas, setAtletas] = useState([]);
  const [categoriaAtiva, setCategoriaAtiva] = useState('Todas');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAtletas = async () => {
      try {
        const res = await atletasAPI.listar();
        setAtletas(res.data);
      } catch (error) {
        console.error('Erro ao carregar atletas:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAtletas();
  }, []);

  const atletasFiltradas = categoriaAtiva === 'Todas'
    ? atletas
    : atletas.filter(a => a.categoria === categoriaAtiva);

  return (
    <div data-testid="equipe-page" className="pt-20">
      {/* Hero */}
      <section className="relative py-24 bg-blue-700 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 hero-pattern" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-yellow-400 font-bold uppercase tracking-widest text-sm">
              Nossas Atletas
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase mt-4">
              Conheça o <span className="text-yellow-400">Time</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Filtros */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {categorias.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategoriaAtiva(cat)}
                data-testid={`filter-${cat.toLowerCase().replace(' ', '-')}`}
                className={`px-6 py-2.5 rounded-full font-bold text-sm uppercase tracking-wider transition-all ${
                  categoriaAtiva === cat
                    ? 'bg-blue-700 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid de Atletas */}
      <section className="py-16 bg-slate-50" data-testid="atletas-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : atletasFiltradas.length === 0 ? (
            <div className="text-center py-20">
              <User className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="font-heading text-2xl text-slate-600 mb-2">
                Nenhuma atleta cadastrada
              </h3>
              <p className="text-slate-500">
                As atletas serão adicionadas em breve.
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {atletasFiltradas.map((atleta, index) => (
                <motion.div
                  key={atleta.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="group relative bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
                  data-testid={`atleta-card-${atleta.id}`}
                >
                  <div className="aspect-[3/4] relative overflow-hidden">
                    {atleta.foto_url ? (
                      <img
                        src={atleta.foto_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${atleta.foto_url}` : atleta.foto_url}
                        alt={atleta.nome}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                        <User className="w-20 h-20 text-blue-400" />
                      </div>
                    )}
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 athlete-overlay opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                      {atleta.bio && (
                        <p className="text-white text-sm line-clamp-3">
                          {atleta.bio}
                        </p>
                      )}
                    </div>

                    {/* Número */}
                    {atleta.numero && (
                      <div className="absolute top-4 right-4 bg-yellow-400 text-blue-900 font-accent text-2xl font-bold w-12 h-12 rounded-full flex items-center justify-center shadow-lg">
                        {atleta.numero}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-5">
                    <h3 className="font-heading text-lg font-bold text-slate-900 uppercase">
                      {atleta.nome}
                    </h3>
                    <p className="text-blue-700 font-medium text-sm">
                      {atleta.posicao}
                    </p>
                    <span className="inline-block mt-2 px-3 py-1 bg-slate-100 rounded-full text-xs text-slate-600">
                      {atleta.categoria}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Join CTA */}
      <section className="py-16 bg-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white uppercase mb-4">
            Quer Fazer Parte do Time?
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            Estamos sempre em busca de novas atletas para fortalecer nossa equipe.
          </p>
          <a
            href="/contato"
            data-testid="join-team-cta"
            className="inline-block bg-yellow-400 text-blue-900 hover:bg-yellow-300 rounded-full px-8 py-4 font-bold uppercase tracking-wider transition-all"
          >
            Entre em Contato
          </a>
        </div>
      </section>
    </div>
  );
}
